import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { Organization } from '@/api/models';
import { OrganizationQueryKeys } from './organizationQueryKeys';

/**
 * The signed-in user's organisation (`GET /organizations/me`): whether
 * onboarding is finished and whether accounting still has to be started.
 *
 * Read once per session: nothing but the onboarding writes changes it, and
 * they invalidate this key, so it never goes stale on its own. Retries follow
 * the app's default.
 *
 * Unwraps the envelope (rulebook §10): `data` is the organisation, and a
 * failed read is the query's `error`, an `ErrorResponse` whose `code` is the
 * server's (`ORGANIZATION_NOT_FOUND` when the token's organisation is gone).
 */
export const useOrganizationQuery = (enabled = true) => {
    const { apiClient } = useApi();

    return useQuery<Organization, ErrorResponse>({
        queryKey: OrganizationQueryKeys.me(),
        queryFn: async () => {
            const result = await apiClient.organizations.getMe();
            if (!result.success) {
                throw result.error;
            }
            return result.response;
        },
        enabled,
        staleTime: Infinity,
    });
};
